import { Pipe, PipeTransform } from '@angular/core';
import { isNil } from 'es-toolkit';
import dayjs from 'dayjs';
import { getEnvaluateColor } from '@shared/utils/utils';
import { truncateStringTail } from '@common';
import {
  MessageType,
  Notice,
  NoticeContentData,
  NoticeContentType,
  NoticeItemKind,
  NoticeMediaItem,
  NoticeStatus,
} from './message.api.service';

/**
 * 消息类型 → 名称 Pipe（messageType：0 默认 1 不懂 2 我来讲）
 *
 * 0 返回空串（模板据此不渲染）；名称与 tags 中的标签名一致，图标复用 EmjioIconNamePipe。
 */
@Pipe({
  name: 'messageTypeName',
  standalone: true,
})
export class MessageTypeNamePipe implements PipeTransform {
  private static readonly NAMES: Partial<Record<MessageType, string>> = {
    [MessageType.Ask]: '不懂',
    [MessageType.Speak]: '我来讲',
  };

  transform(type: MessageType | undefined): string {
    return MessageTypeNamePipe.NAMES[type ?? MessageType.Default] ?? '';
  }
}

/**
 * 任务顶级分类 type → 图标配色 Tailwind 类
 * 1 作业  2 测验  3 考试：白底 + 彩色描边 + 彩色文字
 */
@Pipe({
  name: 'messageType',
  standalone: true,
})
export class MessageTypePipe implements PipeTransform {
  private static readonly CONFIG: Record<number, { label: string; colorClass: string }> = {
    1: { label: '作业', colorClass: 'bg-white border-[#2B70F5] text-[#2B70F5]' },
    2: { label: '测验', colorClass: 'bg-white border-[#13C2C2] text-[#13C2C2]' },
    3: { label: '考试', colorClass: 'bg-white border-[#F5A623] text-[#F5A623]' },
  };
  private static readonly DEFAULT = { label: '消息', colorClass: 'bg-white border-[#D9D9D9] text-[#8C8C8C]' };

  transform(type: number): { label: string; colorClass: string } {
    return MessageTypePipe.CONFIG[type] ?? MessageTypePipe.DEFAULT;
  }
}

/**
 * 得分率 → 评审分档颜色 Pipe（复用系统 getEnvaluateColor）
 * 95/85/75/60 分档：深绿/绿/浅绿/橙/红
 */
@Pipe({
  name: 'evaluateColor',
  standalone: true,
})
export class EvaluateColorPipe implements PipeTransform {
  transform(correctPercent: number | null | undefined): string {
    return getEnvaluateColor(correctPercent ?? 0);
  }
}

/**
 * 班级名「从右往左」截断 Pipe：最长 6 字，超长保留右侧 6 字符 + 左侧省略号前缀
 * 复用公共方法 truncateStringTail（@common）
 * 例：河北26级1班（6字）→ 原样；更长 → 「…26级1班」
 */
@Pipe({
  name: 'classNameTail',
  standalone: true,
})
export class ClassNameTailPipe implements PipeTransform {
  transform(name: string | null | undefined): string {
    return truncateStringTail(name, 6);
  }
}

/**
 * 结构化系统消息 → 渲染视图 Pipe（1557）
 *
 * content 为 { type, data } 的新消息走此分支；旧消息解析失败或类型不在枚举内时返回 null，
 * 模板继续走 extraType 旧逻辑，避免历史消息迁移。
 */
@Pipe({
  name: 'noticeContent',
  standalone: true,
})
export class NoticeContentPipe implements PipeTransform {
  /** 成功态引导行：失败态引导语已包含在 data.detail 中，不重复拼接 */
  private static readonly SUCCESS_GUIDANCE: Partial<Record<NoticeContentType, string>> = {
    [NoticeContentType.Booklet]: '文件保留 7 天，请及时下载。',
    [NoticeContentType.Exam]: '文件保留 7 天，请及时下载。',
    [NoticeContentType.Graph]: '服务器保留 48 小时，请及时下载。',
    [NoticeContentType.Correction]: '感谢您的反馈，题目已更正。',
    [NoticeContentType.Import]: '文件已全部导入。',
  };

  /** 产品文档只允许这三类成功消息给「下载」按钮；转码/纠错/录入/导入均无按钮 */
  private static readonly DOWNLOAD_TYPES: NoticeContentType[] = [
    NoticeContentType.Booklet,
    NoticeContentType.Graph,
    NoticeContentType.Exam,
  ];

  /**
   * 下载产物的服务端保留时长（小时），与成功引导行「保留 N 天/小时」口径一致
   *
   * 过了保留期后下载链接已失效，隐藏「下载」按钮，避免用户点了才报错（bug 18705）。
   */
  private static readonly DOWNLOAD_RETENTION_HOURS: Partial<Record<NoticeContentType, number>> = {
    [NoticeContentType.Booklet]: 7 * 24,
    [NoticeContentType.Exam]: 7 * 24,
    [NoticeContentType.Graph]: 48,
  };

  /** 消息事务名（1557 文档 3.1：标题结构 =【事务 + 状态】+ 对象） */
  private static readonly TRANSACTION_NAMES: Partial<Record<NoticeContentType, string>> = {
    [NoticeContentType.Booklet]: '导出',
    [NoticeContentType.Exam]: '导出',
    [NoticeContentType.Graph]: '生成',
    [NoticeContentType.Transcode]: '转码',
    [NoticeContentType.Correction]: '纠错',
    [NoticeContentType.EdocAssign]: '派发',
    [NoticeContentType.EdocComplete]: '录入',
    [NoticeContentType.Import]: '导入',
  };

  /** 已带状态前缀的标题：后端后续若自行补前缀，前端不再重复拼接 */
  private static readonly TITLE_PREFIX_PATTERN = /^【[^】]*(成功|失败)】/;

  transform(notice: Notice | null | undefined): NoticeView | null {
    if (!notice?.content) return null;

    let parsed: unknown;
    try {
      parsed = JSON.parse(notice.content);
    } catch {
      return null;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    const content = parsed as { type?: unknown; data?: unknown };
    if (!Object.values(NoticeContentType).includes(content.type as NoticeContentType)) return null;
    if (!content.data || typeof content.data !== 'object' || Array.isArray(content.data)) return null;

    const type = content.type as NoticeContentType;
    const data = content.data as NoticeContentData;
    if (!Object.values(NoticeStatus).includes(data.status)) return null;

    const isSuccess = data.status === NoticeStatus.Success;
    const items = NoticeContentPipe.parseItems(data.items);
    const detail = typeof data.detail === 'string' ? data.detail : '';
    const errorMessage = typeof data.error?.message === 'string' ? data.error.message : '';
    const traceback = typeof data.error?.traceback === 'string' ? data.error.traceback : '';

    return {
      type,
      title: NoticeContentPipe.buildTitle(type, data.status, notice.title, detail),
      status: data.status,
      detail,
      guidance: NoticeContentPipe.buildGuidance(type, isSuccess, notice.title, items),
      url: typeof data.url === 'string' ? data.url : '',
      items,
      errorMessage,
      traceback,
      canDownload:
        isSuccess &&
        NoticeContentPipe.DOWNLOAD_TYPES.includes(type) &&
        typeof data.url === 'string' &&
        !!data.url &&
        !NoticeContentPipe.isDownloadExpired(type, notice.createAt),
      canCopy: !!traceback,
    };
  }

  /** 下载链接是否已过服务端保留期；创建时间缺失或非法时不隐藏，避免误伤旧数据 */
  private static isDownloadExpired(type: NoticeContentType, createAt: Date | string): boolean {
    const retentionHours = NoticeContentPipe.DOWNLOAD_RETENTION_HOURS[type];
    if (!retentionHours || !createAt) return false;

    const created = dayjs(createAt);
    if (!created.isValid()) return false;

    return dayjs().isAfter(created.add(retentionHours, 'hour'));
  }

  /** items 兼容早期数据：kind 缺失时，有 duration 视为视频，否则按附件处理 */
  private static parseItems(raw: NoticeMediaItem[] | undefined): NoticeMediaItem[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .filter(item => item && typeof item.name === 'string')
      .map(item => {
        const kind =
          item.kind === NoticeItemKind.Video || (item.kind !== NoticeItemKind.File && !isNil(item.duration))
            ? NoticeItemKind.Video
            : NoticeItemKind.File;
        return {
          kind,
          name: item.name,
          url: typeof item.url === 'string' ? item.url : '',
          cover: item.cover ?? null,
          duration: item.duration,
          size: item.size,
        };
      });
  }

  /** 成功态引导行：转码按就绪产物数量统计；录入类引导句已在 detail 中，不重复追加 */
  private static buildGuidance(
    type: NoticeContentType,
    isSuccess: boolean,
    title: string,
    items: NoticeMediaItem[]
  ): string {
    if (!isSuccess) return '';

    if (type === NoticeContentType.Transcode) {
      // 数量在内容行（data.detail）里，引导行按文档只说明产物已就绪
      const hasVideo = items.some(item => item.kind === NoticeItemKind.Video);
      const hasFile = items.some(item => item.kind === NoticeItemKind.File);
      return [hasVideo ? '视频已就绪，可直接观看。' : '', hasFile ? '附件已就绪，可直接查看。' : ''].join('');
    }

    if (type === NoticeContentType.EdocAssign || type === NoticeContentType.EdocComplete) {
      // 派发/录入完成两个子类型的 detail 已包含引导句，不再追加，避免重复
      return '';
    }

    if (type === NoticeContentType.Edoc) {
      // 契约未下发 edoc 子类型，暂用标题前缀区分：「【派发成功】录入工单」「【录入成功】章节」
      if (title.includes('派发')) return '请及时处理，避免影响后续进度。';
      if (title.includes('录入')) return '课程章节已录入完成。';
      return '';
    }

    return NoticeContentPipe.SUCCESS_GUIDANCE[type] ?? '';
  }

  /**
   * 组装标题：【事务 + 状态】+ 对象（1557 文档 3.1）
   *
   * 后端当前只下发对象名，标题前缀由前端补；若后端后续已按新规范下发则原样返回，避免重复。
   */
  private static buildTitle(
    type: NoticeContentType,
    status: NoticeStatus,
    title: string,
    detail: string
  ): string {
    if (!title || NoticeContentPipe.TITLE_PREFIX_PATTERN.test(title)) return title;

    let transaction = NoticeContentPipe.TRANSACTION_NAMES[type];
    if (type === NoticeContentType.Edoc) {
      // 契约旧值 edoc 无法区分派发/录入，按标题兜底；edoc_assign/edoc_complete 已由上面的映射覆盖
      transaction = title.includes('派发') ? '派发' : '录入';
    }
    if (!transaction) return title;

    let statusText = '成功';
    if (status === NoticeStatus.Failure) {
      // 成绩导入部分失败在契约里仍是 failure，按正文固定引导语识别
      statusText = type === NoticeContentType.Import && detail.includes('部分记录未导入') ? '部分失败' : '失败';
    }
    return `【${transaction}${statusText}】${title}`;
  }
}

/** 结构化系统消息的渲染视图 */
export interface NoticeView {
  type: NoticeContentType;
  /** 完整标题：【事务 + 状态】+ 对象（后端已带前缀时原样） */
  title: string;
  status: NoticeStatus;
  /** 内容行正文（失败时已包含引导语） */
  detail: string;
  /** 成功态引导行，失败态为空 */
  guidance: string;
  url: string;
  items: NoticeMediaItem[];
  errorMessage: string;
  traceback: string;
  canDownload: boolean;
  canCopy: boolean;
}

/**
 * 转码产物条目 → 展示元信息 Pipe
 *
 * 视频给时长与「视频」图标；附件按扩展名给文件图标（颜色沿用语义色卡），未知类型用通用文件图标。
 */
@Pipe({
  name: 'noticeItemMeta',
  standalone: true,
})
export class NoticeItemMetaPipe implements PipeTransform {
  /** 文件扩展名 → 项目现有文件图标（与 fl-attachment-show 使用同一套 assets/images/icon 资源） */
  private static readonly FILE_ICON_PATHS: Record<string, string> = {
    pdf: 'assets/images/icon/pdf.png',
    doc: 'assets/images/icon/word.png',
    docx: 'assets/images/icon/word.png',
    xls: 'assets/images/icon/excel.png',
    xlsx: 'assets/images/icon/excel.png',
    ppt: 'assets/images/icon/ppt.png',
    pptx: 'assets/images/icon/ppt.png',
    jpg: 'assets/images/icon/img.png',
    jpeg: 'assets/images/icon/img.png',
    png: 'assets/images/icon/img.png',
    mp4: 'assets/images/icon/video.png',
    avi: 'assets/images/icon/video.png',
    mp3: 'assets/images/icon/audio.png',
    m4a: 'assets/images/icon/audio.png',
    m4v: 'assets/images/icon/audio.png',
    html: 'assets/images/icon/html.png',
  };
  private static readonly DEFAULT_FILE_ICON = 'assets/images/icon/pdf.png';

  transform(item: NoticeMediaItem): NoticeItemMeta {
    if (item.kind === NoticeItemKind.Video) {
      return {
        isVideo: true,
        iconPath: 'assets/images/icon/video.png',
        metaText: formatDuration(item.duration),
      };
    }
    return {
      isVideo: false,
      iconPath: NoticeItemMetaPipe.FILE_ICON_PATHS[ext(item.name)] ?? NoticeItemMetaPipe.DEFAULT_FILE_ICON,
      metaText: '',
    };
  }
}

/** 取文件扩展名（小写），无扩展名返回空串 */
function ext(name: string): string {
  return name.split('.').pop()?.toLowerCase() ?? '';
}

/** 转码产物条目的展示元信息 */
export interface NoticeItemMeta {
  isVideo: boolean;
  /** 文件类型图标（项目现有 assets/images/icon 资源） */
  iconPath: string;
  /** 视频为时长；附件大小由现有 fmtFileSize Pipe 在模板中格式化 */
  metaText: string;
}

/** 秒 → HH:MM:SS（与设计稿时长格式一致） */
function formatDuration(seconds?: number): string {
  if (isNil(seconds) || seconds < 0) return '';
  const total = Math.round(seconds);
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
}
